import type { QueryKey } from '@tanstack/react-query';

import ClientStateTrigger from './ClientStateTrigger';
import MercuryButton from '@/components/content/button/Button';

import { useQueryActions } from '@/hooks/useQueryActions';

import { OIncidentsStatus } from '../../incidentmanagement/types';
import {
  compactIncidentActionButtonClassNames,
  type IncidentActionButtonVariant,
} from '../../incidentmanagement/components/incidentActionButton';

import ResolveIcon from '@/assets/icons/content/resolved.svg';

interface ResolveProps {
  id: Array<[number, Array<string>]>;
  status: string;
  resource: string;
  previousStatus?: string;
  invalidate?: Array<QueryKey>;
  disabled?: boolean;
  isLoading?: boolean;
  asButton?: boolean;
  buttonVariant?: IncidentActionButtonVariant;
}

const Resolve = ({
  id,
  status,
  previousStatus,
  invalidate,
  resource,
  disabled,
  isLoading,
  asButton,
  buttonVariant = 'default',
}: ResolveProps) => {
  const actions = useQueryActions();

  const onSuccess = () => {
    if (invalidate && invalidate.length) {
      actions.invalidateQueries(invalidate, { type: 'all' });
    }
  };

  const isResolved = status === OIncidentsStatus.Resolved;
  const label = isResolved ? 'Unresolve' : 'Resolve';
  const rollbackStatus =
    previousStatus === OIncidentsStatus.Acknowledged
      ? OIncidentsStatus.Acknowledged
      : OIncidentsStatus.New;
  const nextStatus = isResolved ? rollbackStatus : OIncidentsStatus.Resolved;
  const isCompactOutline = buttonVariant === 'compactOutline';

  return asButton ? (
    <ClientStateTrigger
      updateIds={id}
      resource={resource}
      disabled={disabled || isLoading}
      body={{ status: nextStatus }}
      onSuccess={onSuccess}
    >
      <MercuryButton
        size="sm"
        variant={isCompactOutline ? 'outline' : undefined}
        color={isCompactOutline ? '#454bd1' : 'var(--quo-blue-75)'}
        loading={isLoading}
        rightSection={
          <ResolveIcon width={isCompactOutline ? 16 : 20} height={isCompactOutline ? 16 : 20} />
        }
        classNames={isCompactOutline ? compactIncidentActionButtonClassNames : undefined}
      >
        {label}
      </MercuryButton>
    </ClientStateTrigger>
  ) : (
    <ClientStateTrigger
      updateIds={id}
      icon={<ResolveIcon />}
      label={label}
      size={24}
      resource={resource}
      disabled={disabled || isLoading}
      body={{ status: nextStatus }}
      onSuccess={onSuccess}
    />
  );
};

export default Resolve;
